import { fmtDate } from '@/lib/dates.js';
/**
 * Sprint-4 — Coordinator ICF upload UI (DREAM-22 follow-up).
 *
 * Lives inside the Study Workspace Hub (DREAM-20). Two responsibilities:
 *
 *   1. Show the version history of consent documents for this study, with
 *      the currently-active version flagged.
 *   2. Let an authorised coordinator upload a NEW version. The flow is
 *      presign -> S3 PUT -> finalise, mirroring the Sprint-2 protocol
 *      upload. Activation is automatic on finalise; if there was a
 *      prior active version, DREAM-26 bulk-flags every enrolled subject
 *      for re-consent — so we surface a confirmation modal before
 *      letting the user proceed.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ShieldCheck, FileText, AlertTriangle, MousePointer2, FolderOpen, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { consent, type ConsentDocumentRow, type ConsentApprovalStatus } from '@/api/client';
import { DragDropUpload } from '@/components/ui/DragDropUpload';
import { Modal } from '@/components/ui/Modal';
import { useHasRole } from '@/components/RoleGate';
import { useAuth } from '@/contexts/AuthContext';
import { canSeePage } from '@/lib/permissions';
import { errorText } from '@/lib/permissionError';

// Roles that may open ConsentFieldLayoutEditor. Mirrors EDIT_ROLES in
// pages/consent/ConsentFieldLayoutEditor.tsx — keep in sync. Read-only
// callers (see isStudyReadOnly) never get it.
const CAN_EDIT_LAYOUT_ROLES = [
  'principal_investigator',
  'sub_investigator',
  'coordinator',
  'regulatory_specialist',
  'site_study_designer',
  'site_admin',
] as const;

function statusMeta(status: ConsentApprovalStatus | null | undefined): {
  label: string;
  className: string;
  icon: typeof CheckCircle2;
} {
  switch (status) {
    case 'pending_approval':
      return { label: 'Pending approval', className: 'bg-amber-100 text-amber-800', icon: Clock };
    case 'approved':
      return { label: 'Approved', className: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 };
    case 'rejected':
      return { label: 'Rejected', className: 'bg-danger-100 text-danger-700', icon: XCircle };
    case 'draft':
      return { label: 'Draft', className: 'bg-surface-muted text-ink-secondary', icon: FileText };
    default:
      // Pre-flow rows may report `null` - the backfill script sets these
      // to 'approved' but until it runs we render neutrally rather than
      // showing a bogus "unknown" state.
      return { label: 'Approved', className: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 };
  }
}


// PDF-only — the consent generation + DocuSeal envelope flows both require
// PDF bytes. Backend `/consent/documents/presign` rejects non-PDF mime types
// (see incident 2026-06-23, ae_log.docx case). Keeping the FE accept in sync
// stops users from picking a .docx in the file dialog at all.
const ACCEPT = 'application/pdf';

/**
 * `readOnly`: the caller views the versions but uploads nothing (see
 * isStudyReadOnly). `isStudyPi`: only the PI on this study approves or rejects
 * a version (Harish, 2 Oct 2026).
 */
export function ConsentDocumentsPanel({
  studyId,
  readOnly,
  isStudyPi,
}: {
  studyId: string;
  readOnly: boolean;
  isStudyPi: boolean;
}) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['consent', 'documents', studyId],
    queryFn: () => consent.listDocuments(studyId),
    enabled: !!studyId,
  });
  const docs = data?.documents ?? [];
  const active = docs.find((d) => d.is_active);

  const [file, setFile] = useState<File | null>(null);
  const [versionOverride, setVersionOverride] = useState('');
  const [versionDate, setVersionDate] = useState('');
  const [progress, setProgress] = useState(0);

  const reset = () => {
    setFile(null);
    setVersionOverride('');
    setVersionDate('');
    setProgress(0);
  };

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('No file selected');
      // Phase 1 Item 3 / AC5 — version label mandatory on upload.
      const versionLabel = versionOverride.trim();
      if (!versionLabel) throw new Error('Version label is required (e.g. v1.0 or v2.1).');

      const presigned = await consent.presignDocument({
        study_id: studyId,
        file_name: file.name,
        mime_type: file.type || 'application/pdf',
      });

      await uploadWithProgress(presigned.upload_url, file, file.type || 'application/pdf', setProgress);

      const finalised = await consent.finaliseDocument({
        consent_document_id: presigned.consent_document_id,
        study_id: studyId,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type || 'application/pdf',
        s3_key: presigned.s3_key,
        version_label_override: versionLabel,
        docuseal_template_id: null,
        irb_name: null,
        irb_approval_date: null,
        version_date: versionDate || null,
      });

      return finalised;
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['consent', 'documents', studyId] });
      qc.invalidateQueries({ queryKey: ['signature-queue'] });
      // Feedback #4 - upload no longer auto-activates. New versions
      // land pending PI review; the approve button in the row is the
      // gate that flips is_active and flags re-consent.
      toast.success(`Uploaded ${result.version_label} - waiting for PI approval`);
      reset();
    },
    onError: (e: unknown) => toast.error(`Upload failed: ${e instanceof Error ? e.message : String(e)}`),
  });

  function handleSubmit() {
    if (!file) {
      toast.error('Pick a file first.');
      return;
    }
    // Upload always goes to pending_approval - no confirmation prompt
    // needed. The re-consent flag only fires when the reviewer approves.
    upload.mutate();
  }

  return (
    <section className="rounded border border-surface-border bg-white">
      <header className="flex items-center gap-2 border-b border-surface-border px-4 py-3">
        <ShieldCheck className="h-4 w-4 text-accent-700" />
        <h2 className="text-sm font-semibold text-ink-primary">Informed Consent Documents</h2>
        {active && (
          <span className="ml-auto rounded bg-accent-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-accent-700">
            Active: {active.version_label}
          </span>
        )}
        {active && active.has_drag_drop_layout !== 'true' && (
          <span className="rounded bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800" title="Subjects can’t sign until a signature layout is placed on this version. Open the layout editor to place signature slots.">
            ⚠ No signature layout
          </span>
        )}
      </header>

      {/* Upload form — hidden for read-only viewers */}
      {!readOnly && (
      <div className="space-y-3 border-b border-surface-border p-4">
        <DragDropUpload
          accept={ACCEPT}
          acceptHint="PDF, max 50 MB"
          disabled={upload.isPending}
          onSelect={setFile}
          currentFileName={file?.name ?? null}
          onClear={() => setFile(null)}
        />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block text-xs">
            <span className="font-medium text-ink-secondary">
              Version label <span className="text-red-600">*</span>
            </span>
            <input
              type="text"
              required
              value={versionOverride}
              onChange={(e) => setVersionOverride(e.target.value)}
              placeholder={active ? `next after ${active.version_label}` : 'e.g. v1.0'}
              disabled={upload.isPending}
              className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
            />
          </label>

          <label className="block text-xs">
            <span className="font-medium text-ink-secondary">Version date (printed on ICF)</span>
            <input
              type="date"
              value={versionDate}
              onChange={(e) => setVersionDate(e.target.value)}
              disabled={upload.isPending}
              className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
            />
          </label>
        </div>

        {upload.isPending && (
          <div className="space-y-1">
            <div className="text-[11px] text-ink-muted">
              {progress === 0 ? 'Preparing…' : progress < 100 ? `Uploading… ${progress}%` : 'Finalising…'}
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-muted">
              {progress === 0 ? (
                <div className="h-full w-full animate-pulse bg-accent-400" />
              ) : (
                <div
                  className="h-full bg-accent-500 transition-[width] duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                />
              )}
            </div>
          </div>
        )}

        <button
          type="button"
          disabled={!file || upload.isPending}
          onClick={handleSubmit}
          className="rounded bg-accent-600 px-4 py-2 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-50"
        >
          {upload.isPending ? 'Uploading…' : 'Upload for approval'}
        </button>
        <p className="text-[11px] text-ink-muted">
          Uploaded versions need the study PI's approval before they become active
          and enrolled subjects are flagged for re-consent.
        </p>
      </div>
      )}

      {/* Version history */}
      <div>
        <div className="px-4 pt-3 text-xs font-semibold uppercase tracking-wide text-ink-muted">
          Version history
        </div>
        {isLoading && <div className="px-4 py-6 text-center text-xs text-ink-muted">Loading…</div>}
        {!isLoading && docs.length === 0 && (
          <div className="px-4 py-6 text-center text-xs text-ink-muted">
            No consent documents uploaded for this study yet.
          </div>
        )}
        {docs.length > 0 && (
          <ul className="divide-y divide-surface-border">
            {docs.map((d) => (
              <VersionRow key={d.consent_document_id} doc={d} studyId={studyId} readOnly={readOnly} canApprove={isStudyPi} />
            ))}
          </ul>
        )}
      </div>

    </section>
  );
}

function VersionRow({
  doc,
  studyId,
  readOnly,
  canApprove,
}: {
  doc: ConsentDocumentRow;
  studyId: string;
  readOnly: boolean;
  canApprove: boolean;
}) {
  const qc = useQueryClient();
  const { user } = useAuth();
  // "Open in Binder" only for roles that can open the binder.
  const canOpenBinder = !!user && canSeePage(user.role, 'binder');
  const canEditLayout = useHasRole(CAN_EDIT_LAYOUT_ROLES) && !readOnly;
  const [opening, setOpening] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');

  const approveM = useMutation({
    mutationFn: () => consent.approveDocument(doc.consent_document_id),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['consent', 'documents', studyId] });
      qc.invalidateQueries({ queryKey: ['signature-queue'] });
      if (result.re_consent_flagged && result.re_consent_flagged > 0) {
        toast.success(`Approved ${result.version_label} - ${result.re_consent_flagged} subject(s) flagged for re-consent`);
      } else {
        toast.success(`Approved ${result.version_label}`);
      }
      setShowApproveConfirm(false);
    },
    onError: (e: unknown) => {
      toast.error(errorText(e, 'Approval failed'));
    },
  });

  const rejectM = useMutation({
    mutationFn: () => consent.rejectDocument(doc.consent_document_id, { notes: rejectNotes.trim() }),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['consent', 'documents', studyId] });
      toast.success(`Rejected ${result.version_label}`);
      setShowRejectConfirm(false);
      setRejectNotes('');
    },
    onError: (e: unknown) => {
      toast.error(errorText(e, 'Rejection failed'));
    },
  });

  async function open() {
    setOpening(true);
    try {
      const r = await consent.documentDownloadUrl(doc.consent_document_id);
      window.open(r.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error(`Open failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setOpening(false);
    }
  }

  const status = doc.approval_status ?? 'approved';
  const meta = statusMeta(status);
  const MetaIcon = meta.icon;
  const isPending = status === 'pending_approval';

  return (
    <li className="flex items-center gap-3 px-4 py-2.5 text-xs">
      <FileText className="h-4 w-4 text-ink-muted" />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-ink-primary">{doc.version_label}</span>
          <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold ${meta.className}`}>
            <MetaIcon className="h-2.5 w-2.5" />
            {meta.label}
          </span>
          {doc.is_active && (
            <span className="rounded bg-accent-100 px-1.5 py-0.5 text-[10px] font-semibold text-accent-700">Active</span>
          )}
          {doc.is_active && doc.has_drag_drop_layout !== 'true' && (
            <span
              className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800"
              title="Subjects can't sign until you place signature fields via the layout editor."
            >
              No signature layout
            </span>
          )}
          {canEditLayout && status === 'approved' && (
            <Link
              to={`/consent-documents/${doc.consent_document_id}/field-layout`}
              className="inline-flex items-center gap-0.5 rounded border border-accent-300 bg-accent-50 px-1.5 py-0.5 text-[10px] font-semibold text-accent-700 hover:bg-accent-100"
              title={
                doc.has_drag_drop_layout === 'true'
                  ? 'Edit signature/coordinator field placement on the PDF'
                  : 'Place signature/coordinator fields on the PDF so subjects can sign'
              }
            >
              <MousePointer2 className="h-2.5 w-2.5" />
              {doc.has_drag_drop_layout === 'true' ? 'Edit layout' : 'Place signatures'}
            </Link>
          )}
          {doc.docuseal_template_id && (
            <span className="rounded bg-surface-muted px-1.5 py-0.5 text-[10px] font-mono text-ink-muted">
              tpl:{doc.docuseal_template_id}
            </span>
          )}
          {status === 'approved' && canOpenBinder && (
            <Link
              to="/binder"
              className="inline-flex items-center gap-0.5 rounded border border-surface-border bg-surface-muted/40 px-1.5 py-0.5 text-[10px] font-medium text-ink-secondary hover:bg-surface-muted"
              title="Open the eISF binder - this ICF is mirrored to Participant Materials -> Blank Forms -> Blank ICF"
            >
              <FolderOpen className="h-2.5 w-2.5" />
              Open in Binder
            </Link>
          )}
        </div>
        <div className="truncate text-[11px] text-ink-muted">
          {doc.file_name} · {Math.round(doc.file_size / 1024)} KB · uploaded {fmtDate(doc.uploaded_at)}
        </div>
        {(doc.irb_name || doc.irb_approval_date || doc.version_date) && (
          <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-ink-muted">
            {doc.version_date && <span>Version date: {fmtDate(doc.version_date)}</span>}
            {doc.irb_name && <span>IRB: {doc.irb_name}</span>}
            {doc.irb_approval_date && <span>Approved: {fmtDate(doc.irb_approval_date)}</span>}
          </div>
        )}
        {status === 'rejected' && doc.approval_notes && (
          <div className="mt-1 rounded border border-danger-200 bg-danger-50 px-2 py-1 text-[11px] text-danger-700">
            <span className="font-semibold">Reviewer notes: </span>{doc.approval_notes}
          </div>
        )}
      </div>

      <div className="flex items-center gap-1">
        {isPending && canApprove && (
          <>
            <button
              type="button"
              onClick={() => setShowApproveConfirm(true)}
              className="rounded bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700"
              disabled={approveM.isPending}
            >
              Approve
            </button>
            <button
              type="button"
              onClick={() => setShowRejectConfirm(true)}
              className="rounded border border-danger-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-danger-700 hover:bg-danger-50"
              disabled={rejectM.isPending}
            >
              Reject
            </button>
          </>
        )}
        <button
          type="button"
          onClick={open}
          disabled={opening}
          className="rounded border border-surface-border px-2 py-1 text-[11px] font-medium hover:bg-surface-muted/50 disabled:opacity-50"
        >
          {opening ? '…' : 'View'}
        </button>
      </div>

      <Modal
        open={showApproveConfirm}
        onOpenChange={setShowApproveConfirm}
        title={`Approve consent version ${doc.version_label}?`}
        width={480}
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 text-sm text-ink-secondary">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div className="space-y-2">
              <p>
                Approving will activate <span className="font-semibold text-ink-primary">{doc.version_label}</span>,
                supersede any prior active version, and flag every currently-enrolled subject
                on this study for re-consent.
              </p>
              <p>This is logged in the audit trail and cannot be reverted.</p>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => setShowApproveConfirm(false)} className="btn-ghost" disabled={approveM.isPending}>
              Cancel
            </button>
            <button
              type="button"
              onClick={() => approveM.mutate()}
              disabled={approveM.isPending}
              className="rounded bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:opacity-50"
            >
              {approveM.isPending ? 'Approving…' : 'Yes, approve & activate'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={showRejectConfirm}
        onOpenChange={(open) => { setShowRejectConfirm(open); if (!open) setRejectNotes(''); }}
        title={`Reject consent version ${doc.version_label}?`}
        width={480}
      >
        <div className="space-y-4">
          <p className="text-sm text-ink-secondary">
            Rejection is terminal - this version will never activate. The coordinator must upload a new draft.
            Notes are required and will be shown to the coordinator.
          </p>
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-muted">Rejection notes</span>
            <textarea
              rows={3}
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              className="w-full rounded border border-surface-border bg-white px-2 py-1.5 text-sm"
              placeholder="e.g. IRB approval date missing on page 3; re-upload with the updated version stamp."
            />
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => setShowRejectConfirm(false)} className="btn-ghost" disabled={rejectM.isPending}>
              Cancel
            </button>
            <button
              type="button"
              onClick={() => rejectM.mutate()}
              disabled={rejectM.isPending || rejectNotes.trim().length === 0}
              className="rounded bg-danger-700 px-4 py-2 text-sm font-medium text-white hover:bg-danger-800 disabled:opacity-50"
            >
              {rejectM.isPending ? 'Rejecting…' : 'Reject version'}
            </button>
          </div>
        </div>
      </Modal>
    </li>
  );
}


function uploadWithProgress(url: string, file: File, contentType: string, onProgress: (p: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.setRequestHeader('x-amz-server-side-encryption', 'AES256');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(100);
        resolve();
      } else {
        reject(new Error(`S3 PUT failed: ${xhr.status} ${xhr.statusText}`));
      }
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(file);
  });
}
